import { useState, useCallback, useRef, useEffect } from "react";
import type { BundlePrice } from "../utils/priceCalculator";
import type { FlexPriceResponse } from "../services/flexPriceService";
import { calculateFlexPrice } from "../services/flexPriceService";
import { useDebouncedCallback } from "use-debounce";

interface UseBundlePriceOptions {
	configBundleId: string;
	initialPrice: BundlePrice;
	initialDiscount: BundlePrice;
	taxRate?: number;
	subGroupId?: string;
	hasFlexAvailable: boolean;
}

interface UseBundlePriceReturn {
	currentPrice: BundlePrice;
	flexPrice: FlexPriceResponse | null;
	updatePrice: (newPrice: BundlePrice) => void;
	isCalculatingFlex: boolean;
}

/**
 * Hook to manage bundle price state and flex price calculations
 * Handles dynamic price updates when components are changed
 */
export function useBundlePrice({
	configBundleId,
	initialPrice,
	initialDiscount,
	taxRate,
	subGroupId,
	hasFlexAvailable,
}: UseBundlePriceOptions): UseBundlePriceReturn {
	const [currentPrice, setCurrentPrice] = useState<BundlePrice>(initialPrice);
	const [flexPrice, setFlexPrice] = useState<FlexPriceResponse | null>(null);
	const [isCalculatingFlex, setIsCalculatingFlex] = useState(false);

	const initialDiscountRef = useRef(initialDiscount);

	useEffect(() => {
		initialDiscountRef.current = initialDiscount;
	}, [initialDiscount]);

	useEffect(() => {
		setCurrentPrice(initialPrice);
	}, [initialPrice]);

	const calculateFlexPriceForPrice = useCallback(
		async (price: BundlePrice) => {
			if (!hasFlexAvailable || !taxRate || !subGroupId) {
				return;
			}

			try {
				const priceNet = (price?.net ?? 0) + (initialDiscountRef.current?.net ?? 0);

				const result = await calculateFlexPrice({
					productId: configBundleId,
					subGroupId: subGroupId!,
					priceNet: priceNet,
					initialDiscountNet: price.net,
					taxRate: taxRate!,
				});

				setFlexPrice(result);
				setIsCalculatingFlex(false);
			} catch (error) {
				console.error("Failed to calculate flex price:", error);
				setFlexPrice(null);
				setIsCalculatingFlex(false);
			}
		},
		[configBundleId, subGroupId, taxRate, hasFlexAvailable],
	);

	const calculateFlexPriceDebounced = useDebouncedCallback(
		(price: BundlePrice) => {
			calculateFlexPriceForPrice(price);
		},
		200,
	);

	useEffect(() => {
		if (hasFlexAvailable && taxRate && subGroupId) {
			calculateFlexPriceForPrice(initialPrice);
		}
	}, [hasFlexAvailable, taxRate, subGroupId, configBundleId]);

	const updatePrice = useCallback(
		(newPrice: BundlePrice) => {
			setCurrentPrice(newPrice);
			setIsCalculatingFlex(true);
			calculateFlexPriceDebounced(newPrice);
		},
		[calculateFlexPriceDebounced],
	);

	return {
		currentPrice,
		flexPrice,
		updatePrice,
		isCalculatingFlex,
	};
}
			